import React, { useEffect } from 'react'
import { Input, Button, Form, notification, Carousel } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons'
import { history } from 'index'
import { getLoggedInUserInfo, getGifUrl } from 'utils'
import Cookies from 'js-cookie'
import { useSelector, useDispatch } from 'react-redux'
import { login } from 'services/axios/auth'
import { fetchTeamPendingLeaveRequest } from 'redux/empLeave/action'
import { getEventsNotification } from 'services/axios/events'
import { isEmpty } from 'lodash'
import { userRoleEnums } from 'enums/userRole'
import style from '../style.module.scss'

const Login = () => {
  const [form] = Form.useForm()
  const isLoading = useSelector((state) => state?.settings?.isLoading)
  const dispatch = useDispatch()
  const teamPendingLeave = useSelector((state) => state?.empLeaveData?.teamPendingLeave)

  useEffect(() => {
    const userRole = getLoggedInUserInfo()?.userRole
    if (userRole && isLoading) {
      history.push('/dashboard')
    }
  }, [])

  useEffect(() => {
    const userRole = getLoggedInUserInfo()?.userRole
    if (userRole) {
      if (teamPendingLeave > 0) {
        notification.success({
          message: 'Leave Requests Pending',
          description: 'Employees leave request are pending',
        })
      }
    }
  }, [teamPendingLeave])

  const showEventNotifications = (events) => {
    if (events.length === 0) return
    notification.info({
      message: '🎉 Events 🎉',
      description: (
        <Carousel autoplay autoplaySpeed={4000} style={{ textAlign: 'center' }}>
          {events.map(({ eventName, gifUrl }, index) => (
            <div key={index} style={{ padding: '10px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0' }}>
                {eventName}
              </h3>
              <img
                src={gifUrl}
                alt={`${eventName} GIF`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '350px',
                  borderRadius: '15px',
                  margin: '20px auto',
                }}
              />
            </div>
          ))}
        </Carousel>
      ),
      duration: 10 * events.length, // Adjust duration to fit all slides
      style: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '600px',
        padding: '20px',
        borderRadius: '20px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
        backgroundColor: '#ffffff',
      },
    })
  }

  const onFinish = async (values) => {
    try {
      const response = await login(values)
      const { data } = response
      const { token, permissions, user } = data

      if (token) {
        Cookies.set('accessToken', token, { expires: 1 })
        Cookies.set('permissions', JSON.stringify(permissions), { expires: 1 })

        const userInformation = getLoggedInUserInfo()
        userInformation.email = user
        const { userRole, userId } = userInformation

        if (isEmpty(permissions) && userRole !== userRoleEnums?.SUPER_ADMIN) {
          notification.warning({
            message: 'Warning',
            description: 'Please contact your administrator',
          })
        } else {
          await dispatch(fetchTeamPendingLeaveRequest(userId))

          // Navigate to the dashboard or attendance page first
          if (userRole === 'trainee') {
            history.push('/attendance')
          } else {
            history.push('/dashboard')
          }

          notification.success({
            message: 'Success',
            description: data?.message,
          })

          // Fetch and display events after navigation
          setTimeout(async () => {
            const eventsResponse = await getEventsNotification()
            const eventData = eventsResponse?.data?.eventData

            if (eventData && eventData.length > 0) {
              const loginDate = new Date(
                new Date().getFullYear(),
                data?.loginDate?.month - 1,
                data?.loginDate?.day,
              )

              const matchingEvents = eventData
                .filter((event) => {
                  const eventStartDate = new Date(event?.eventStartDate)
                  return (
                    eventStartDate.getMonth() === loginDate.getMonth() &&
                    eventStartDate.getDate() === loginDate.getDate()
                  )
                })
                .map((event) => ({
                  eventName: event.eventName,
                  gifUrl: getGifUrl(event.eventName),
                }))

              if (matchingEvents.length > 0) {
                showEventNotifications(matchingEvents)
              }
            }
          }, 1000)
        }
      }
    } catch (error) {
      console.error('Error during login:', error)
      notification.error({
        message: 'Login Failed',
        description: 'There was an issue logging you in. Please try again.',
      })
    }
  }

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo)
  }

  return (
    <div>
      <div className="pt-2 pb-5 text-center">
        <div className={style.logo}>
          <h2>Value Portal</h2>
        </div>
      </div>
      <div className={`card ${style.container}`}>
        <div className="text-dark font-size-32 mb-3">Sign In</div>
        <div className="mb-4">Sign in with credentials</div>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="mb-4"
          initialValues={{ email: '', password: '' }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Please input your email id' }]}
          >
            <Input placeholder="Email" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please input your password' }]}
          >
            <Input.Password
              placeholder="Password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
          <div className="mb-4">
            <a href="/auth/forgot-password">Forget Password?</a>
          </div>
          <Button
            type="primary"
            className="text-center w-100 btn btn-success"
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
          >
            <strong>Sign in</strong>
          </Button>
        </Form>
      </div>
    </div>
  )
}

export default Login
